"use client";
import { Button } from "@/app/components/shadcn/button";
import { SectionItem } from "@/app/lib/features/home/home.types";
import Image from "next/image";
import Link from "next/link";
import { FC } from "react";
import { trackCategoryClick } from "@/app/lib/tracking/interactions";

interface CategoryCardProps {
  category: SectionItem;
}

const CategoryCard: FC<CategoryCardProps> = ({ category }) => {
  const getSocialIcon = () => {
    const iconClass = "w-3 h-3";
    return (
      <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    );
  };

  const mainImage = category.image;
  const categoryName =
    category.label || category.name || category.title || "Kategori";
  const categoryLink = category.link || category.slug || "#";
  const socialUsername = category.name?.startsWith("@") ? category.name : null;
  const subImages = category.sub_images || [];

  return (
    <Link
      href={categoryLink}
      className="group bg-gray-300 block rounded-t-md overflow-hidden w-full h-full"
      onClick={(e) => {
        if (category.id) trackCategoryClick(category.id);
        if (category.type === "custom" && category.link) {
          e.preventDefault();
          window.open(category.link, "_blank");
        }
      }}
    >
      <div className="relative w-full aspect-square overflow-hidden">
        <div className="relative w-full h-full flex items-center justify-center">
          <Image
            src={mainImage}
            alt={categoryName}
            fill
            className="object-cover group-hover:scale-[1.02] transition-all duration-300 ease-in-out"
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
            quality={70}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.opacity = "0";
            }}
          />
        </div>

        {socialUsername && (
          <div className="absolute top-2 right-2">
            <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-linear-to-r from-purple-500 to-pink-500 text-white">
              {getSocialIcon()}
              <span className="truncate max-w-16 hidden sm:block">
                {socialUsername}
              </span>
            </div>
          </div>
        )}

        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 w-full px-4">
          <Button
            size="lg"
            variant="secondary"
            className="w-full bg-white/60 backdrop-blur-sm border border-white/50 hover:bg-white/90"
          >
            {categoryName}
          </Button>
        </div>
      </div>

      {subImages.length > 0 && (
        <div className="flex gap-1 p-2 rounded-md h-48.75">
          {subImages.map((imageUrl: string, index: number) => (
            <div key={index} className="relative w-full h-full">
              <div className="relative w-full h-full rounded overflow-hidden">
                <Image
                  src={imageUrl}
                  alt={`${categoryName} ${index + 1}`}
                  fill
                  className="object-cover group-hover:scale-[1.02] transition-all duration-300 ease-in-out"
                  sizes="(max-width: 640px) 16vw, (max-width: 1024px) 11vw, 8vw"
                  quality={70}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.opacity = "0";
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </Link>
  );
};

export default CategoryCard;
